import { createWithEqualityFn } from 'zustand/traditional';

import {
  RecommenderDocument,
  RecommenderExtendedProfile,
  RecommenderProfile,
  UserProfile,
} from '@youscience/commonapp-service-common';

import useNotificationStore from '@store/NotificationStore';

import { commonAppService } from '@services/commonApp.service';
import { UPDATE_ERROR_NOTIFICATION } from '@pages/StudentDetailsPage/components/common/formUtils';
import useAppInitStore from '@store/AppInitStore';

interface SaveRecommenderProfileOptions {
  updateForms?: Record<string, boolean>;
  showNotification?: boolean;
}

export interface RecommenderStore {
  lastUpdated: number;
  userProfile: UserProfile | undefined;
  recommenders: RecommenderDocument[];
  recommenderProfile: RecommenderExtendedProfile;
  getRecommenders: () => Promise<RecommenderDocument[]>;
  getRecommenderProfile: () => Promise<RecommenderExtendedProfile>;
  saveRecommenderProfile: (
    recommenderProfile: Partial<RecommenderProfile>,
    page: string,
    options?: SaveRecommenderProfileOptions,
  ) => Promise<void>;
  getUserProfile: () => Promise<void>;
}

const initialState = {
  lastUpdated: Date.now(),
} as RecommenderStore;

export const useRecommenderStore = createWithEqualityFn<RecommenderStore>()(
  (set) => ({
    ...initialState,

    getRecommenders: async () => {
      const recommenders = await commonAppService.getAsync<RecommenderDocument[]>(`recommenders`);

      set({
        recommenders,
      });

      return recommenders;
    },

    getRecommenderProfile: async () => {
      const { isInitialized } = useAppInitStore.getState();

      if (!isInitialized) {
        await new Promise<void>((resolve) => {
          const unsubscribe = useAppInitStore.subscribe((state) => {
            if (state.isInitialized) {
              unsubscribe();
              resolve();
            }
          });
        });
      }

      const recommenderProfile = await commonAppService.getAsync<RecommenderExtendedProfile>(`recommenderProfile`);

      set({
        recommenderProfile,
      });

      return recommenderProfile;
    },

    saveRecommenderProfile: async (
      recommenderProfile: Partial<RecommenderProfile>,
      page: string,
      options: SaveRecommenderProfileOptions = {},
    ) => {
      const { updateForms, showNotification = false } = options;

      const data = {
        ...recommenderProfile,
        updateForms,
      };

      try {
        const updatedRecommenderProfile = await commonAppService.patchAsync<
          RecommenderExtendedProfile,
          RecommenderProfile
        >(`recommenderProfile`, data);

        set({
          recommenderProfile: updatedRecommenderProfile,
          lastUpdated: Date.now(),
        });

        if (showNotification) {
          useNotificationStore.getState().notify({ message: `${page} updates have been saved`, severity: 'success' });
        }
      } catch (error) {
        useNotificationStore.getState().notify(UPDATE_ERROR_NOTIFICATION);
      }
    },

    getUserProfile: async () => {
      const userProfile = await commonAppService.getAsync<UserProfile>(`user/profile`);

      set({
        userProfile,
      });
    },
  }),

  Object.is,
);
